import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import insightsApi from '../../api/insightsApi';
import { canEditProjectContent } from '../../utils/projectRoles';

// ── "What to do next" ────────────────────────────────────────────────────────
// The By-group single view's recommended actions for ONE selection, under the
// focus card. Each action targets one impact in the Register; it can be edited
// inline and pushed to the Activity Dashboard as an activity under that impact.
//
// The state lives in the parent, keyed by `reportId:scope_key`, so switching
// selections and back neither refetches nor loses an edit in progress:
//   entry     undefined | { loading } | { error: { status } }
//             | { data, drafts, editing, busy, failed }
//   drafts    { [index]: { title, description } }  — edited fields

const INK = '#16335c';
const TEXT = '#33445c';
const MUTED = '#6b7a90';
const FAINT = '#8493a6';
const ACCENT = '#1565c0';
const ACCENT_DARK = '#0d47a1';
const DANGER = '#c53434';
const EMPTY_STATUSES = ['no_impacts', 'no_groups'];

export const freshActionsEntry = (data) => ({ data, drafts: {}, editing: {}, busy: {}, failed: {} });

// The action as it would be sent: the BE's fields, overridden by any edit.
export const editedAction = (entry, action) => ({ ...action, ...(entry?.drafts?.[action.index] || {}) });

const indexed = (actions) => (actions || []).map((action, i) => ({ ...action, index: action.index ?? i }));

const activityDashboardHref = (projectId) => `/projects/${projectId}?view=activity`;

function Shell({ children, projectId, testId = 'group-actions-card' }) {
    return (
        <Box
            data-testid={testId}
            sx={{
                mt: 2,
                px: { xs: 2, sm: 2.75 },
                py: { xs: 2, sm: 2.25 },
                borderRadius: 3,
                border: '1px solid rgba(21, 101, 192, 0.14)',
                bgcolor: '#fff',
                boxShadow: '0 8px 18px rgba(13, 71, 161, 0.04)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                <PlaylistAddCheckOutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
                <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: ACCENT_DARK, flex: 1 }}>
                    <FormattedMessage id="groupActions.title" defaultMessage="What to do next" />
                </Typography>
                {projectId != null && (
                    <Button
                        data-testid="group-actions-dashboard-link"
                        component="a"
                        href={activityDashboardHref(projectId)}
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        <FormattedMessage id="groupActions.go_to_dashboard" defaultMessage="Go to Activity Dashboard" />
                    </Button>
                )}
            </Box>
            {children}
        </Box>
    );
}

// `readOnly` (a project viewer) shows the recommendation and whether it was
// added, without Edit or Add — adding creates an activity, which they can't.
function ActionItem({ action, entry, onEdit, onToggleEdit, onAdd, readOnly = false }) {
    const intl = useIntl();
    const edited = editedAction(entry, action);
    const added = action.added_activity_id != null;
    const editing = Boolean(entry.editing?.[action.index]) && !added;
    const busy = Boolean(entry.busy?.[action.index]);
    const target = [action.impact_title, action.group_name].filter(Boolean).join(' · ');

    return (
        <Box
            component="li"
            data-testid="group-action-item"
            sx={{ py: 1.75, '& + li': { borderTop: '1px solid rgba(21, 101, 192, 0.12)' }, '&:last-of-type': { pb: 0.5 } }}
        >
            {editing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <TextField
                        size="small"
                        label={intl.formatMessage({ id: 'groupActions.field_title', defaultMessage: 'Title' })}
                        value={edited.title}
                        onChange={(e) => onEdit({ title: e.target.value })}
                        inputProps={{ 'data-testid': 'group-action-title-input' }}
                        fullWidth
                    />
                    <TextField
                        size="small"
                        multiline
                        minRows={2}
                        label={intl.formatMessage({ id: 'groupActions.field_description', defaultMessage: 'Description' })}
                        value={edited.description || ''}
                        onChange={(e) => onEdit({ description: e.target.value })}
                        inputProps={{ 'data-testid': 'group-action-description-input' }}
                        fullWidth
                    />
                </Box>
            ) : (
                <>
                    <Typography data-testid="group-action-title" sx={{ fontSize: 15, fontWeight: 700, color: INK, lineHeight: 1.4 }}>
                        {edited.title}
                    </Typography>
                    {edited.description && (
                        <Typography data-testid="group-action-description" variant="body2" sx={{ color: '#47566b', mt: 0.5, lineHeight: 1.6 }}>
                            {edited.description}
                        </Typography>
                    )}
                </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.1 }}>
                {target && (
                    <Typography data-testid="group-action-impact" variant="caption" sx={{ color: MUTED }}>
                        <FormattedMessage
                            id="groupActions.addresses"
                            defaultMessage="Addresses: {impact}"
                            values={{ impact: <Box component="span" sx={{ fontWeight: 600, color: TEXT }}>{target}</Box> }}
                        />
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {!added && !readOnly && (
                    <Button
                        data-testid="group-action-edit"
                        size="small"
                        disabled={busy}
                        onClick={onToggleEdit}
                        startIcon={editing ? <CheckIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {editing
                            ? <FormattedMessage id="groupActions.done_editing" defaultMessage="Done" />
                            : <FormattedMessage id="groupActions.edit" defaultMessage="Edit" />}
                    </Button>
                )}
                {(!readOnly || added) && (
                <Button
                    data-testid="group-action-add"
                    size="small"
                    variant={added ? 'text' : 'outlined'}
                    disabled={added || busy || !String(edited.title || '').trim()}
                    onClick={onAdd}
                    startIcon={busy ? <CircularProgress size={14} /> : added ? <CheckIcon fontSize="small" /> : null}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                    {added
                        ? <FormattedMessage id="groupActions.added" defaultMessage="Added ✓" />
                        : <FormattedMessage id="groupActions.add" defaultMessage="Add to Activity Dashboard" />}
                </Button>
                )}
            </Box>
            {entry.failed?.[action.index] && (
                <Typography data-testid="group-action-add-failed" variant="caption" sx={{ display: 'block', color: DANGER, mt: 0.5 }}>
                    <FormattedMessage id="groupActions.add_failed" defaultMessage="Couldn't add this activity. Please try again." />
                </Typography>
            )}
        </Box>
    );
}

export default function GroupActionsCard({ reportId, scopeKey, entry, onLoad, update }) {
    const data = entry?.data;
    const projectId = data?.project_id ?? null;
    const ready = data?.status === 'ready';
    // The plan carries the caller's role on its project; a viewer reads it.
    const readOnly = !canEditProjectContent(data?.my_role);

    // Fetch on first sight of this selection only; the parent keeps the entry.
    useEffect(() => {
        if (!entry) onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, scopeKey, entry]);

    if (!entry || entry.loading) {
        return (
            <Shell>
                <Box data-testid="group-actions-loading" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: FAINT }}>
                        <FormattedMessage id="groupActions.loading" defaultMessage="Generating recommendations…" />
                    </Typography>
                </Box>
            </Shell>
        );
    }

    if (entry.error) {
        // A selection the BE does not serve has nothing to recommend.
        if (entry.error.status === 404) return null;
        return (
            <Shell>
                <Box data-testid="group-actions-error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: DANGER }}>
                        <FormattedMessage id="groupActions.failed" defaultMessage="Couldn't load the recommendations." />
                    </Typography>
                    <Button data-testid="group-actions-retry" size="small" onClick={onLoad} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        <FormattedMessage id="groupActions.retry" defaultMessage="Retry" />
                    </Button>
                </Box>
            </Shell>
        );
    }

    if (EMPTY_STATUSES.includes(data?.status)) {
        return (
            <Shell projectId={projectId}>
                <Typography data-testid="group-actions-empty" variant="body2" sx={{ color: MUTED }}>
                    <FormattedMessage
                        id="groupActions.empty_no_impacts"
                        defaultMessage="To see what to do next, fill out the impact assessment for this group first."
                    />
                </Typography>
            </Shell>
        );
    }

    const actions = indexed(data?.actions);
    if (!ready || actions.length === 0) return null;

    const patchSlot = (slot, index, value) => update((prev) => ({ ...prev, [slot]: { ...prev[slot], [index]: value } }));

    const add = async (action) => {
        const { title, description } = editedAction(entry, action);
        patchSlot('busy', action.index, true);
        patchSlot('failed', action.index, false);
        let created;
        try {
            created = await insightsApi.createActivity(projectId, action.impact_id, {
                title: title.trim(),
                description: (description || '').trim(),
            });
        } catch (err) {
            patchSlot('busy', action.index, false);
            patchSlot('failed', action.index, true);
            return;
        }
        // The activity exists from here on; a failed PATCH only means the plan
        // forgets it was added, so the item still reads "Added" — re-adding
        // would make a duplicate.
        try {
            await insightsApi.markGroupActionAdded(reportId, { scope_key: scopeKey, index: action.index, activity_id: created?.id });
        } catch (err) {
            // logged by the API wrapper
        }
        update((prev) => ({
            ...prev,
            busy: { ...prev.busy, [action.index]: false },
            editing: { ...prev.editing, [action.index]: false },
            data: {
                ...prev.data,
                actions: indexed(prev.data.actions).map((item) => (
                    item.index === action.index ? { ...item, added_activity_id: created?.id ?? true } : item
                )),
            },
        }));
    };

    return (
        <Shell projectId={projectId}>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {actions.map((action) => (
                    <ActionItem
                        key={action.index}
                        action={action}
                        entry={entry}
                        onEdit={(fields) => update((prev) => {
                            const { title, description } = editedAction(prev, action);
                            return {
                                ...prev,
                                drafts: { ...prev.drafts, [action.index]: { title, description, ...fields } },
                            };
                        })}
                        onToggleEdit={() => patchSlot('editing', action.index, !entry.editing?.[action.index])}
                        onAdd={() => add(action)}
                        readOnly={readOnly}
                    />
                ))}
            </Box>
        </Shell>
    );
}
